import { createAppError } from '../../api/errors';
import type { MutationResult } from '../../api/contract';
import type { PageResult } from '../../shared/data/types';
import {
  commissionRuleActorId,
  commissionRuleFixtures,
  commissionRuleId,
  createCommissionRulePreview,
} from './fixtures';
import type { CommissionRulesRepository } from './repository';
import type {
  CancelRuleInput,
  CommissionRuleCreateInput,
  CommissionRuleDetail,
  CommissionRuleListQuery,
  CommissionRulePreview,
  CommissionRulePreviewInput,
  PublishRuleInput,
} from './types';

export interface MockCommissionRulesOptions {
  readonly overlap?: boolean;
  readonly createFailures?: number;
  readonly lifecycleFailures?: number;
  readonly empty?: boolean;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const commissionRuleDetailAliases: Readonly<Record<string, string>> = {
  'rule-a': commissionRuleFixtures[0]!.id,
};

export class MockCommissionRulesRepository implements CommissionRulesRepository {
  readonly createIdempotencyKeys: string[] = [];
  readonly publishIdempotencyKeys: string[] = [];
  readonly cancelIdempotencyKeys: string[] = [];
  private readonly overlap: boolean;
  private createFailures: number;
  private lifecycleFailures: number;
  private rules: CommissionRuleDetail[];

  constructor(options: MockCommissionRulesOptions = {}) {
    this.overlap = options.overlap ?? false;
    this.createFailures = options.createFailures ?? 0;
    this.lifecycleFailures = options.lifecycleFailures ?? 0;
    this.rules = options.empty ? [] : structuredClone([...commissionRuleFixtures]);
  }

  async list(
    query: CommissionRuleListQuery,
    signal: AbortSignal,
  ): Promise<PageResult<CommissionRuleDetail>> {
    assertNotAborted(signal);
    const filtered = this.rules.filter((rule) =>
      Object.entries(query.filters).every(([key, value]) => {
        if (value === '') return true;
        if (key === 'gameTypeId') return rule.game.gameType.id === value;
        return rule[key as keyof CommissionRuleDetail] === value;
      }),
    );
    const start = Math.max(0, query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize),
      total: filtered.length,
      requestId: 'mock-commission-rules-list',
    };
  }

  async get(ruleId: string, signal: AbortSignal): Promise<CommissionRuleDetail> {
    assertNotAborted(signal);
    const resolvedId = commissionRuleDetailAliases[ruleId] ?? ruleId;
    const rule = this.rules.find((item) => item.id === resolvedId);
    if (rule === undefined) throw mockError('NOT_FOUND', 'mock-commission-rule-get');
    return structuredClone(rule);
  }

  async preview(input: CommissionRulePreviewInput): Promise<CommissionRulePreview> {
    return createCommissionRulePreview(input, this.overlap);
  }

  async create(
    input: CommissionRuleCreateInput,
    idempotencyKey: string,
  ): Promise<MutationResult<CommissionRuleDetail>> {
    this.createIdempotencyKeys.push(idempotencyKey);
    if (this.createFailures > 0) {
      this.createFailures -= 1;
      throw mockError('TEMPORARY_UNAVAILABLE', 'mock-commission-rule-create');
    }
    const template = structuredClone(commissionRuleFixtures[0]!);
    const rule: CommissionRuleDetail = {
      ...template,
      id: commissionRuleId,
      scope: input.scope,
      gameId: input.gameId,
      partnerId: input.scope === 'PARTNER' ? (input.partnerId ?? null) : null,
      activationMode: input.activationMode,
      effectiveFrom: input.effectiveFrom ?? null,
      effectiveTo: input.effectiveTo ?? null,
      agentRatePercent: input.agentRatePercent,
      affiliateRatePercent: input.affiliateRatePercent,
      version: this.rules.length + 1,
      lifecycle: 'DRAFT',
      phase: 'DRAFT',
      effective: false,
      dependencyBlockers: [],
      createdAt: '2026-07-23T11:00:00+07:00',
      createdBy: commissionRuleActorId,
      publishedAt: null,
      publishedBy: null,
      cancelledAt: null,
      cancelledBy: null,
      cancellationReason: null,
    };
    this.rules = [rule, ...this.rules];
    return { data: structuredClone(rule), requestId: 'mock-commission-rule-create' };
  }

  async publish(
    ruleId: string,
    _input: PublishRuleInput,
    idempotencyKey: string,
  ): Promise<MutationResult<CommissionRuleDetail>> {
    this.publishIdempotencyKeys.push(idempotencyKey);
    if (this.lifecycleFailures > 0) {
      this.lifecycleFailures -= 1;
      throw mockError('TEMPORARY_UNAVAILABLE', 'mock-commission-rule-publish');
    }
    return this.updateLifecycle(ruleId, {
      lifecycle: 'PUBLISHED',
      phase: 'SCHEDULED',
      publishedAt: '2026-07-23T12:00:00+07:00',
      publishedBy: commissionRuleActorId,
    });
  }

  async cancel(
    ruleId: string,
    input: CancelRuleInput,
    idempotencyKey: string,
  ): Promise<MutationResult<CommissionRuleDetail>> {
    this.cancelIdempotencyKeys.push(idempotencyKey);
    if (this.lifecycleFailures > 0) {
      this.lifecycleFailures -= 1;
      throw mockError('TEMPORARY_UNAVAILABLE', 'mock-commission-rule-cancel');
    }
    return this.updateLifecycle(ruleId, {
      lifecycle: 'CANCELLED',
      phase: 'CANCELLED',
      cancelledAt: '2026-07-23T12:00:00+07:00',
      cancelledBy: commissionRuleActorId,
      cancellationReason: input.reason,
    });
  }

  private updateLifecycle(
    ruleId: string,
    update: Partial<CommissionRuleDetail>,
  ): MutationResult<CommissionRuleDetail> {
    const index = this.rules.findIndex((item) => item.id === ruleId);
    if (index < 0) throw mockError('NOT_FOUND', 'mock-commission-rule-lifecycle');
    const rule = { ...this.rules[index]!, ...update };
    this.rules = this.rules.map((item, itemIndex) => (itemIndex === index ? rule : item));
    return { data: structuredClone(rule), requestId: 'mock-commission-rule-lifecycle' };
  }
}

function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

function mockError(code: string, requestId: string) {
  return createAppError({
    status: code === 'NOT_FOUND' ? 404 : 503,
    code,
    message: 'Internal mock API details',
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
